import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Target, Minus } from "lucide-react";

/**
 * Small badge visualising the gap between theoretical and actual
 * man-hours on a chantier.
 *
 * Props:
 *  - delta: backend `time_delta` object
 *    { theoretical_hours, actual_hours, delta_hours, delta_percent, status }
 *  - size: 'sm' | 'md'
 *  - compact: only show delta value + icon (no theoretical reminder)
 */
export const TimeDeltaBadge = ({ delta, size = "sm", compact = false }) => {
  if (!delta || delta.status === "no_target") {
    return (
      <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-300" data-testid="time-delta-no-target">
        <Target className="h-3 w-3 mr-1" /> Sans cible
      </Badge>
    );
  }

  const { theoretical_hours, actual_hours, delta_hours, delta_percent, status } = delta;

  const config = {
    over:         { color: "bg-red-100 text-red-800 border-red-300", Icon: TrendingUp,   label: "Dépassement" },
    slight_over:  { color: "bg-amber-100 text-amber-800 border-amber-300", Icon: TrendingUp, label: "Léger dépassement" },
    on_target:    { color: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: Target, label: "À l'équilibre" },
    slight_under: { color: "bg-teal-100 text-teal-800 border-teal-300", Icon: TrendingDown, label: "Légère avance" },
    under:        { color: "bg-green-100 text-green-800 border-green-300", Icon: TrendingDown, label: "Gain de temps" },
  }[status] || { color: "bg-slate-100 text-slate-700 border-slate-300", Icon: Minus, label: "—" };

  const { Icon } = config;
  const sign = delta_hours > 0 ? "+" : "";
  const valueText = `${sign}${delta_hours} h (${sign}${delta_percent}%)`;

  return (
    <Badge
      variant="outline"
      className={`${config.color} ${size === "md" ? "text-sm px-2 py-1" : "text-xs"} font-mono gap-1`}
      title={`${config.label} — Réel ${actual_hours} h / Théorique ${theoretical_hours} h`}
      data-testid={`time-delta-${status}`}
    >
      <Icon className="h-3 w-3" />
      {compact ? valueText : `${config.label} · ${valueText}`}
    </Badge>
  );
};

export default TimeDeltaBadge;
